import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { UpgradeModule } from '@angular/upgrade/static';
import { ConfigService } from './config/config.service';
import { Ng1TranslateService } from './shared/ng1-translate.service';
import { LanguageService } from './shared/services/language.service';

import { RavenErrorHandler } from './config/raven.error.handler';
import { SentryService } from './config/sentry.service';
import { appRoutes, EmptyComponent } from './app.routes';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { NewAppComponent } from './main/main.component';
import { BlacklistModule } from './blacklist/blacklist.module';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { SharedModule } from './shared/shared.module';
import { CustomUrlHandling } from './config/url.handling';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { NgProgressModule } from '@ngx-progressbar/core';
import { ProgressInterceptor } from './config/progress.inteceptor';
import { AuthorizedHeaderInterceptor } from './config/authorized-header.interceptor';
import { BaseUrlInterceptor } from './config/base-url.interceptor';
import { HttpErrorsInterceptor } from './config/http-errors.interceptor';
import { CrazyPopupGeneratorModule } from './lead-generation/lead-generation.module';
import { NgxPermissionsModule } from 'ngx-permissions';
import { DynamicModule } from 'ng-dynamic-component';
import { LeftMenuComponent } from './main/left-menu/left-menu.component';
import { CalendlyService } from './config/calendly.service';
import { IntegrationsModule } from './integrations/integrations.module';
import { CampaignSettingsModule } from './campaign-settings/campaign-settings.module';
import { PhoneNumbersModule } from './phone-numbers/phone-numbers.module';
import { ReportsModule } from './reports/reports.module';
import { ContactsModule } from './contacts/contacts.module';
import { AccountService } from './shared/services/account.service';
import { PaymentService } from './shared/services/payment.service';
import { AlertPaymentService } from './shared/services/alert-payment.service';
import { PricingNumbersService } from './shared/services/pricing-numbers.service';
import { MobileAppModule } from './mobile-app/mobile-app.module';
import { IvrModule } from './ivr/ivr.module';
import { AccountModule } from './account/account.module';
import { Ng1LiveChatService } from './shared/ng1/ng1-live-chat.service';
import { WideAlertComponent } from './wide-alert/wide-alert.component';
import { Ng1AuthService } from './shared/ng1/ng1-auth.service';
import { MyAccountModule } from './my-account/my-account.module';
import { ChannelsLeftMenuComponent } from './main/left-menu/channels-left-menu/channels-left-menu.component';
import { Ng1StripeElementsService } from './shared/ng1/ng1-stripe-elements.service';
import { WebhooksModule } from 'app/webhooks/webhooks.module';
import { GetNumberModule } from './get-number/get-number.module';
import { ContactDetailsModule } from './contact-details/contact-details.module';
import { CallHistoryModule } from './call-history/call-history.module';
import { DetailsModule } from './details/details.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { ApiModule } from './api/api.module';
import { WidgetModule } from './widget/widget.module';
import { AccountsModule } from './accounts/accounts.module';
import { PricingModule } from './pricing/pricing.module';
import { UsersModule } from './users/users.module';
import { ExistsNumberModule } from './exists-number/exists-number.module';
import { Ng1AccountService } from './shared/ng1/ng1-account.service';
import { AddressBookModule } from './address-book/address-book.module';
import { OnboardingModule } from './onboarding/onboarding.module';
import { DefaultKeyMissingTranslationHelper } from './shared/translations/DefaultKeyMissingTranslationHelper';
import { FirstStepsModule } from './first-steps/first-steps.module';
import { AvatarComponent, AvatarModule } from 'ngx-avatar';

@NgModule({
  declarations: [
    AppComponent,
    NewAppComponent,
    LeftMenuComponent,
    EmptyComponent,
    WideAlertComponent,
    ChannelsLeftMenuComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    UpgradeModule,
    SharedModule,
    BlacklistModule,
    CrazyPopupGeneratorModule,
    IntegrationsModule,
    AccountModule,
    TranslateModule.forRoot({
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: DefaultKeyMissingTranslationHelper }
    }),
    NgProgressModule.forRoot(),
    NgxPermissionsModule.forRoot(),
    HttpClientModule,
    DynamicModule.withComponents([LeftMenuComponent]),
    CampaignSettingsModule,
    DashboardModule,
    PhoneNumbersModule,
    ReportsModule,
    ContactsModule,
    MobileAppModule,
    MyAccountModule,
    WebhooksModule,
    CallHistoryModule,
    IvrModule,
    GetNumberModule,
    ContactDetailsModule,
    DetailsModule,
    ApiModule,
    WidgetModule,
    AccountsModule,
    PricingModule,
    UsersModule,
    ExistsNumberModule,
    AddressBookModule,
    OnboardingModule,
    FirstStepsModule,
    AvatarModule,
    RouterModule.forRoot(appRoutes, {
      preloadingStrategy: PreloadAllModules,
      onSameUrlNavigation: 'reload'
    })
  ],
  providers: [
    RavenErrorHandler,
    SentryService,
    ConfigService,
    Ng1TranslateService,
    LanguageService,
    ProgressInterceptor,
    AuthorizedHeaderInterceptor,
    BaseUrlInterceptor,
    HttpErrorsInterceptor,
    CalendlyService,
    AccountService,
    PaymentService,
    AlertPaymentService,
    PricingNumbersService,
    CustomUrlHandling,
    Ng1LiveChatService,
    Ng1AuthService,
    Ng1AccountService,
    Ng1StripeElementsService
  ],
  bootstrap: [AppComponent],
  entryComponents: [NewAppComponent, LeftMenuComponent, AvatarComponent]
})
export class AppModule {
  constructor(public upgrade: UpgradeModule) {}
}
